import { Typography, TypographyProps, Box } from '@mui/material'
import { forwardRef } from 'react'

/**
 * Section heading alignment
 */
export type SectionHeadingAlign = 'left' | 'center' | 'right'

/**
 * SectionHeading component props
 */
export interface SectionHeadingProps extends Omit<TypographyProps, 'variant' | 'component' | 'align'> {
  /** Section heading text */
  children: React.ReactNode
  /** Text alignment */
  align?: SectionHeadingAlign
  /** Optional subtitle below heading */
  subtitle?: string
  /** Show decorative underline */
  underline?: boolean
}

/**
 * Standardized section heading component
 * Provides consistent typography and optional decorative elements
 */
export const SectionHeading = forwardRef<HTMLHeadingElement, SectionHeadingProps>(
  ({ children, align = 'center', subtitle, underline = false, sx, ...props }, ref) => {
    return (
      <Box
        sx={{
          textAlign: align,
          mb: 4,
          ...sx,
        }}
      >
        <Typography
          ref={ref}
          variant="h2"
          component="h2"
          gutterBottom={!!subtitle || underline}
          {...props}
        >
          {children}
        </Typography>
        {underline && (
          <Box
            sx={{
              width: 60,
              height: 4,
              bgcolor: 'primary.main',
              borderRadius: 2,
              mx: align === 'center' ? 'auto' : 0,
              ml: align === 'right' ? 'auto' : align === 'center' ? 'auto' : 0,
              mb: subtitle ? 2 : 0,
            }}
          />
        )}
        {subtitle && (
          <Typography
            variant="h6"
            component="p"
            color="text.secondary"
            sx={{ mt: underline ? 0 : 1 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    )
  }
)

SectionHeading.displayName = 'SectionHeading'
